import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Lock, AlertTriangle } from "lucide-react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ClosedData {
  closedDate: Date;
  salePrice: string;
  financingType: string;
}

interface PropertyStatusFormProps {
  propertyId: string;
  status: string;
  contractExpirationDate: Date;
  closedData: ClosedData;
  isLoading: boolean;
  wasSoldOnLoad?: boolean;
  wasPendingOnLoad?: boolean;
  wasDraftOnLoad?: boolean;
  onStatusChange: (status: string) => void;
  onContractExpirationDateChange: (date: Date | undefined) => void;
  onClosedDateChange: (date: Date | undefined) => void;
  onClosedDataChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const PropertyStatusForm = ({
  propertyId,
  status,
  contractExpirationDate,
  closedData,
  isLoading,
  wasSoldOnLoad = false,
  wasPendingOnLoad = false,
  wasDraftOnLoad = false,
  onStatusChange,
  onContractExpirationDateChange,
  onClosedDateChange,
  onClosedDataChange,
  onSubmit
}: PropertyStatusFormProps) => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [showSoldConfirmation, setShowSoldConfirmation] = useState(false);
  const [showAdminOverrideConfirmation, setShowAdminOverrideConfirmation] = useState(false);
  const [showReactivateConfirmation, setShowReactivateConfirmation] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<React.FormEvent | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);

  // Check if current user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsAdmin(false);
          setIsCheckingAdmin(false);
          return;
        }

        const { data, error } = await supabase
          .from("user_roles" as any)
          .select("role")
          .eq("user_id", user.id)
          .eq("role", "admin")
          .single();

        setIsAdmin(!error && !!data);
      } catch {
        setIsAdmin(false);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);

  const handleSubmitClick = (e: React.FormEvent) => {
    e.preventDefault();

    // Non-admin sellers cannot save while status is "expired" — they must pick Active or Pending
    if (status === "expired" && !isAdmin) {
      toast.error("This listing has expired. Choose Active or Pending with a future contract expiration date to reactivate it.");
      return;
    }

    // Validate contract expiration date for active/pending properties
    if (status === "active" || status === "pending") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expDate = new Date(contractExpirationDate);
      expDate.setHours(0, 0, 0, 0);
      
      if (expDate <= today) {
        toast.error("Contract expiration date must be in the future for active and pending properties.");
        return;
      }
    }
    
    // If changing FROM pending to active, show reactivation confirmation
    if (wasPendingOnLoad && status === "active") {
      setPendingSubmit(e);
      setShowReactivateConfirmation(true);
      return;
    }
    
    // If changing FROM sold status (admin only), show confirmation
    if (wasSoldOnLoad && status !== "sold" && isAdmin) {
      setPendingSubmit(e);
      setShowAdminOverrideConfirmation(true);
      return;
    }
    
    // If marking as sold for first time, show confirmation
    if (status === "sold" && !wasSoldOnLoad) {
      setPendingSubmit(e);
      setShowSoldConfirmation(true);
      return;
    }
    
    onSubmit(e);
  };

  const handleConfirmSold = () => {
    setShowSoldConfirmation(false);
    if (pendingSubmit) {
      onSubmit(pendingSubmit);
      setPendingSubmit(null);
    }
  };

  const handleConfirmAdminOverride = () => {
    setShowAdminOverrideConfirmation(false);
    if (pendingSubmit) {
      onSubmit(pendingSubmit);
      setPendingSubmit(null);
    }
  };

  const handleConfirmReactivate = async () => {
    setShowReactivateConfirmation(false);
    setIsReactivating(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('cancel-property-offer-on-reactivation', {
        body: { propertyId },
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success("Property reactivated successfully. The buyer has been notified.");
        navigate(`/seller/properties/${propertyId}`);
      } else {
        throw new Error(data.error || "Failed to reactivate property");
      }
    } catch (error: any) {
      console.error('Error reactivating property:', error);
      toast.error(error.message || "Failed to reactivate property");
    } finally {
      setIsReactivating(false);
      setPendingSubmit(null);
    }
  };

  // Locked state for non-admin users on sold properties
  const isLockedForSeller = wasSoldOnLoad && !isAdmin && !isCheckingAdmin;

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Property Status</h2>
            <Separator />
            
            <div className="space-y-4">
              {/* Locked message for non-admin on sold properties */}
              {isLockedForSeller && (
                <div className="flex items-start gap-3 p-4 bg-muted/50 border border-border rounded-md">
                  <Lock className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Status Locked</p>
                    <p className="text-sm text-muted-foreground">
                      This property has been marked as sold and cannot be changed. 
                      Sold properties serve as market comparables for buyers.
                    </p>
                    {closedData.closedDate && (
                      <p className="text-sm text-muted-foreground mt-2">
                        <strong>Closed:</strong> {format(closedData.closedDate, "PPP")}
                        {closedData.salePrice && ` • Sale Price: $${Number(closedData.salePrice).toLocaleString()}`}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Admin warning for sold properties */}
              {wasSoldOnLoad && isAdmin && !isCheckingAdmin && (
                <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-md">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-500">Admin Override Available</p>
                    <p className="text-sm text-muted-foreground">
                      This property is sold. As an admin, you can change its status, but this will affect market comparable data.
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="status">Current Status</Label>
                <Select 
                  value={status}
                  onValueChange={onStatusChange}
                  disabled={isLockedForSeller}
                >
                  <SelectTrigger id="status" className="bg-muted">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {wasDraftOnLoad ? (
                      <>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="sold">Sold</SelectItem>
                        {isAdmin && <SelectItem value="expired">Expired</SelectItem>}
                      </>
                    )}
                  </SelectContent>
              </Select>
              {wasDraftOnLoad && (
                <p className="text-sm text-muted-foreground">
                  This listing is a draft. Select Active and set a future contract expiration date, then click Activate Listing.
                </p>
              )}
              {status === "expired" && !isAdmin && (
                <p className="text-sm text-amber-500">
                  This listing expired. Choose Active or Pending with a future contract expiration date to reactivate it.
                </p>
              )}
              </div>

              {/* Contract Expiration Date for active/pending status */}
              {(status === "active" || status === "pending") && !isLockedForSeller && (
                <div className="border rounded-md border-border p-4 space-y-4">
                  <h3 className="font-medium">Contract Expiration</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contractExpirationDate">Contract Expiration Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal bg-muted"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {contractExpirationDate
                            ? format(contractExpirationDate, "PPP")
                            : "Select a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={contractExpirationDate}
                          onSelect={onContractExpirationDateChange}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date <= today;
                          }}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-sm text-muted-foreground">
                      Must be a future date for active and pending properties
                    </p>
                  </div>
                </div>
              )}

              {/* Warning when selecting sold for the first time */}
              {status === "sold" && !wasSoldOnLoad && (
                <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-md">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-500">Important Notice</p>
                    <p className="text-sm text-muted-foreground">
                      Marking a property as sold is permanent. Once sold, you will not be able to change the status back. 
                      This data will be used as market comparables for buyers.
                    </p>
                  </div>
                </div>
              )}
              
              {status === "sold" && !isLockedForSeller && (
                <div className="border rounded-md border-border p-4 space-y-4">
                  <h3 className="font-medium">Closed Deal Information</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="closedDate">Closed Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal bg-muted"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {closedData.closedDate
                            ? format(closedData.closedDate, "PPP")
                            : "Select a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={closedData.closedDate}
                          onSelect={onClosedDateChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="salePrice">Sale Price</Label>
                    <Input
                      id="salePrice"
                      name="salePrice"
                      value={closedData.salePrice}
                      onChange={onClosedDataChange}
                      placeholder="$0.00"
                      className="bg-muted"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="financingType">Financing Type</Label>
                    <Select 
                      value={closedData.financingType} 
                      onValueChange={(value) => onClosedDataChange({ 
                        target: { name: 'financingType', value } 
                      } as any)}
                    >
                      <SelectTrigger id="financingType" className="bg-muted">
                        <SelectValue placeholder="Select financing type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Hard Money">Hard Money</SelectItem>
                        <SelectItem value="Private Money">Private Money</SelectItem>
                        <SelectItem value="Conventional">Conventional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {!isLockedForSeller && (() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const expDate = contractExpirationDate ? new Date(contractExpirationDate) : null;
                if (expDate) expDate.setHours(0, 0, 0, 0);
                const hasFutureExp = !!expDate && expDate > today;
                const canActivate = wasDraftOnLoad && status === "active" && hasFutureExp;
                return (
                  <div className="pt-4 flex justify-end space-x-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(`/seller/properties/${propertyId}`, { replace: true })}
                    >
                      Cancel
                    </Button>
                    {wasDraftOnLoad ? (
                      <Button
                        type="button"
                        className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
                        disabled={isLoading || isReactivating || !canActivate}
                        onClick={handleSubmitClick}
                      >
                        {isLoading || isReactivating ? "Activating..." : "Activate Listing"}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
                        disabled={isLoading || isReactivating}
                        onClick={handleSubmitClick}
                      >
                        {isLoading || isReactivating ? "Saving..." : "Save Status"}
                      </Button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation dialog for marking as sold */}
      <AlertDialog open={showSoldConfirmation} onOpenChange={setShowSoldConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Sold Status
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark this property as sold? This action is permanent and cannot be undone by sellers. 
              The property will be used as a market comparable for buyers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingSubmit(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmSold}
              className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
            >
              Confirm Sold
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin override confirmation dialog */}
      <AlertDialog open={showAdminOverrideConfirmation} onOpenChange={setShowAdminOverrideConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Admin Override Confirmation
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are changing the status of a sold property. This will affect market comparable data used by buyers. 
              Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingSubmit(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmAdminOverride}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Confirm Override
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivation confirmation dialog */}
      <AlertDialog open={showReactivateConfirmation} onOpenChange={setShowReactivateConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Reactivate Property
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the accepted offer and notify the buyer that the deal fell through. 
              The property will go back on the market and all buyers will be able to make new offers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingSubmit(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmReactivate}
              className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
              disabled={isReactivating}
            >
              {isReactivating ? "Processing..." : "Confirm Reactivation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
